import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchCnae } from "../../services/cnaeService";
import GeneralIcon from "../../assets";

const initialCnaes = [
  { code: "47.23-7-00", description: "Comércio varejista de bebidas" },
  {
    code: "47.52-1-00",
    description:
      "Comércio varejista especializado de equipamentos e suprimentos de informática",
  },
  { code: "41.20-4-00", description: "Construção de edifícios" },
  { code: "56.11-2-01", description: "Restaurantes e similares" },
  {
    code: "62.01-5-01",
    description: "Desenvolvimento de programas de computador sob encomenda",
  },
];

export default function Step5CNAE() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCnaes, setSelectedCnaes] = useState([]);
  const [availableCnaes, setAvailableCnaes] = useState(initialCnaes);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await searchCnae(searchTerm, 1, 5);
      setAvailableCnaes(res.items);
      setPage(res.page);
      setTotalPages(res.totalPages);
    } catch (err) {
      setError(err.message || "Falha ao buscar CNAEs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial load with 5 items per page
    (async () => {
      setLoading(true);
      try {
        const res = await searchCnae("", 1, 5);
        setAvailableCnaes(res.items);
        setPage(res.page);
        setTotalPages(res.totalPages);
      } catch (err) {
        setError(err.message || "Falha ao carregar CNAEs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleApplyCnae = (cnae) => {
    setSelectedCnaes([...selectedCnaes, cnae]);
    setAvailableCnaes(availableCnaes.filter((item) => item.code !== cnae.code));
  };

  const handleRemoveCnae = (cnae) => {
    setSelectedCnaes(selectedCnaes.filter((item) => item.code !== cnae.code));
    setAvailableCnaes([cnae, ...availableCnaes]);
  };

  const handleFinishCreation = () => {
    try {
      const { saveStepData, submitCreation } = require("../../services/storeCreationService");
      saveStepData("step5", { cnaes: selectedCnaes });
      submitCreation().then(() => {
        navigate("/dashboard");
      });
    } catch {
      navigate("/dashboard");
    }
  };

  const handleNext = async () => {
    if (page < totalPages) {
      setLoading(true);
      try {
        const res = await searchCnae(searchTerm, page + 1, 5);
        setAvailableCnaes(res.items);
        setPage(res.page);
        setTotalPages(res.totalPages);
      } catch (err) {
        setError(err.message || "Falha ao paginar CNAEs");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePrevious = async () => {
    if (page > 1) {
      setLoading(true);
      try {
        const res = await searchCnae(searchTerm, page - 1, 5);
        setAvailableCnaes(res.items);
        setPage(res.page);
        setTotalPages(res.totalPages);
      } catch (err) {
        setError(err.message || "Falha ao paginar CNAEs");
      } finally {
        setLoading(false);
      }
    }
  };

  const isCnaeSelected = (code) => selectedCnaes.some((c) => c.code === code);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
      <div className="w-full max-w-lg space-y-6 pt-10 pb-10">
        <div className="space-y-2">
          <button type="button" onClick={() => navigate(-1)} className="text-gray-600">&larr;</button>
          <h1 className="text-xl font-bold text-gray-800">Criar loja</h1>
          <h2 className="text-xl font-bold text-gray-800">
            Adicione os CNAEs da sua loja
          </h2>
          <p className="text-sm text-gray-600">
            CNAE (Classificação Nacional de Atividades Econômicas) é o código
            que identifica a atividade principal e secundárias de uma empresa. É
            importante porque define obrigações fiscais, tributos e quais tipos
            de notas fiscais a empresa pode emitir.
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex space-x-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Pesquisar por código/nome de CNAE"
          />
          <button
            type="submit"
            className="bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition flex items-center justify-center"
          >
            <GeneralIcon iconName="search" alt="search" className="w-5 h-5" />
          </button>
        </form>

        <div className="flex flex-wrap gap-2">
          {selectedCnaes.map((cnae) => (
            <div
              key={cnae.code}
              className="bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-md flex items-center space-x-2"
            >
              <span>{cnae.code}</span>
              <span>{cnae.description}</span>
              <button
                type="button"
                onClick={() => handleRemoveCnae(cnae)}
                className="text-purple-700 hover:text-purple-900 font-bold"
              >
                Remover
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {availableCnaes.map((cnae) => (
            <div
              key={cnae.code}
              className="border border-gray-200 rounded-lg p-3 flex justify-between items-center"
            >
              <div>
                <p className="font-semibold text-gray-800">{cnae.code}</p>
                <p className="text-sm text-gray-600">{cnae.description}</p>
              </div>

              {isCnaeSelected(cnae.code) ? (
                <button
                  type="button"
                  onClick={() => handleRemoveCnae(cnae)}
                  className="bg-red-100 text-red-600 py-2 px-4 rounded-lg font-semibold hover:bg-red-200 transition text-sm"
                >
                  Remover
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleApplyCnae(cnae)}
                  className="bg-purple-100 text-purple-600 py-2 px-4 rounded-lg font-semibold hover:bg-purple-200 transition text-sm"
                >
                  Aplicar
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-6">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={page <= 1 || loading}
            className={`py-3 px-6 rounded-lg font-semibold transition w-1/3 ${page > 1 && !loading ? 'bg-purple-400 text-white hover:bg-purple-500' : 'bg-purple-200 text-white cursor-not-allowed'}`}
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600">Página {page}</span>
          <button
            type="button"
            onClick={handleNext}
            disabled={page >= totalPages || loading}
            className={`py-3 px-6 rounded-lg font-semibold transition w-1/3 ${page < totalPages && !loading ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-200 text-white cursor-not-allowed'}`}
          >
            Próximo
          </button>
        </div>

        <button
          type="button"
          onClick={handleFinishCreation}
          className="w-full bg-purple-400 text-white py-3 rounded-lg font-semibold hover:bg-purple-500 transition mt-4"
        >
          Criar loja
        </button>

        {loading && (
          <p className="text-sm text-gray-500 mt-2">Carregando...</p>
        )}
        {error && (
          <p className="text-sm text-red-500 mt-2">{error}</p>
        )}
      </div>
    </div>
  );
}
